import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@/shared/components/ui/Input';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { CampaignStatsCards } from '../components/CampaignStatsCards';
import { CampaignProgressChart } from '../components/CampaignProgressChart';
import { CampaignMessagesTable } from '../components/CampaignMessagesTable';
import {
  useCampaign,
  useCampaignStats,
  useCampaignMessages,
  useDuplicateCampaign,
  useActivateCampaign,
  usePauseCampaign,
  useResumeCampaign,
  useCancelCampaign,
  useSendTestMessage,
} from '../hooks/useCampaigns';
import { campaignsService } from '../services/campaignsService';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS, AUDIENCE_TYPE_LABELS, SCHEDULING_TYPE_LABELS } from '@troia-v3/types';
import { renderTemplateSeguro, valoresDeExemplo } from '@/modules/templates/components/templateRender';
import type {
  TemplateDropdownDetail,
  CampaignMessageResponse,
} from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { useTemplateDropdownDetail } from '@/modules/templates/hooks/useTemplateDropdownDetail';

/**
 * Campaign Detail Page
 * Shows campaign stats, progress chart, messages table, and test send functionality.
 * Layout: 3-zone toolbar with breadcrumb (never back-arrow) + identity header + sections.
 */

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--theme-card-background)',
  border: '1px solid var(--theme-card-border)',
  borderRadius: 12,
  overflow: 'hidden',
};

const cardHeadStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--theme-card-border)',
  color: 'var(--theme-card-text)',
};

/**
 * Corpo do template para o card "Conteúdo" — pelo renderizador único, o mesmo
 * do chat, da campanha e da tela de criação. A versão anterior era mais uma
 * cópia do "ache o componente BODY" e tratava QUALQUER canal desconhecido como
 * e-mail, devolvendo o assunto.
 */
const getTemplateBodyText = (template?: TemplateDropdownDetail): string | null => {
  const render = renderTemplateSeguro(
    template?.providerConfig,
    valoresDeExemplo(template?.variables),
    template?.variables,
  );
  if (!render) return null;
  if (render.canal === 'email') return render.assunto || render.corpo || null;
  return render.corpo || null;
};

export const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const confirm = useConfirm();

  // State
  const [showTestSection, setShowTestSection] = useState(false);
  const [testPhones, setTestPhones] = useState('');
  const [testResults, setTestResults] = useState<Array<{ recipientIdentifier: string; success: boolean; error?: string }> | null>(null);
  const [messagesSearch, setMessagesSearch] = useState('');
  const [messagesStatusFilter, setMessagesStatusFilter] = useState('');
  const [messagesPage, setMessagesPage] = useState(1);

  // Queries
  const { data: campaign, isLoading: campaignLoading, error: campaignError } = useCampaign(id);
  const { data: stats } = useCampaignStats(id);
  const { data: messagesData, isLoading: messagesLoading } = useCampaignMessages(id, {
    page: messagesPage,
    limit: 20,
    status: messagesStatusFilter || undefined,
    search: messagesSearch || undefined,
  });
  const { data: template } = useTemplateDropdownDetail(campaign?.templateId);

  // Mutations
  const activateCampaign = useActivateCampaign();
  const pauseCampaign = usePauseCampaign();
  const resumeCampaign = useResumeCampaign();
  const cancelCampaign = useCancelCampaign();
  const duplicateCampaign = useDuplicateCampaign();
  const sendTestMessage = useSendTestMessage();
  const [exporting, setExporting] = useState(false);

  // Socket.IO real-time updates
  const handleSocketRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['campaign-stats', id] });
    queryClient.invalidateQueries({ queryKey: ['campaign-messages', id] });
  }, [queryClient, id]);

  const handleCampaignCompleted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['campaign-stats', id] });
    queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    queryClient.invalidateQueries({ queryKey: ['campaign-messages', id] });
  }, [queryClient, id]);

  useEffect(() => {
    if (!socket || !id) return;

    socket.on(SOCKET_EVENTS.CAMPAIGN_PROGRESS, handleSocketRefetch);
    socket.on(SOCKET_EVENTS.CAMPAIGN_COMPLETED, handleCampaignCompleted);
    socket.on(SOCKET_EVENTS.CAMPAIGN_MESSAGE_STATUS, handleSocketRefetch);

    return () => {
      socket.off(SOCKET_EVENTS.CAMPAIGN_PROGRESS, handleSocketRefetch);
      socket.off(SOCKET_EVENTS.CAMPAIGN_COMPLETED, handleCampaignCompleted);
      socket.off(SOCKET_EVENTS.CAMPAIGN_MESSAGE_STATUS, handleSocketRefetch);
    };
  }, [socket, id, handleSocketRefetch, handleCampaignCompleted]);

  // Handlers
  const handleActivate = async () => {
    if (!id) return;
    if (await confirm({ message: 'Tem certeza que deseja ativar esta campanha?', variant: 'default', confirmButtonText: 'Ativar' })) {
      await activateCampaign.mutateAsync(id);
    }
  };

  const handleDuplicate = async () => {
    if (!id) return;
    const copy = await duplicateCampaign.mutateAsync(id);
    navigate(`/prospecting/campaigns/${copy.id}/edit`);
  };

  // Exporta TODAS as mensagens da campanha (varre a paginação) num CSV client-side.
  const handleExport = async () => {
    if (!id || exporting) return;
    setExporting(true);
    try {
      const all: CampaignMessageResponse[] = [];
      let currentPage = 1;
      const limit = 200;
      for (;;) {
        const res = await campaignsService.getCampaignMessages(id, { page: currentPage, limit });
        all.push(...res.items);
        if (!res.hasNextPage || res.items.length === 0 || currentPage >= 500) break;
        currentPage += 1;
      }
      const esc = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const header = ['Destinatário', 'Telefone', 'Status', 'Enviada', 'Entregue', 'Lida', 'Erro'];
      const rows = all.map((m) => [
        m.recipientName || '',
        m.recipientIdentifier || '',
        m.status,
        m.sentAt || '',
        m.deliveredAt || '',
        m.readAt || '',
        m.failureReason || '',
      ]);
      const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
      const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(campaign?.name || 'campanha').replace(/[^\w.-]+/g, '_')}-mensagens.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handlePause = async () => {
    if (!id) return;
    if (await confirm({ message: 'Tem certeza que deseja pausar esta campanha?', variant: 'default', confirmButtonText: 'Pausar' })) {
      await pauseCampaign.mutateAsync(id);
    }
  };

  const handleResume = async () => {
    if (!id) return;
    if (await confirm({ message: 'Tem certeza que deseja retomar esta campanha?', variant: 'default', confirmButtonText: 'Retomar' })) {
      await resumeCampaign.mutateAsync(id);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    if (await confirm({ message: 'Tem certeza que deseja cancelar esta campanha? Esta ação não pode ser desfeita.', variant: 'default', confirmButtonText: 'Cancelar campanha' })) {
      await cancelCampaign.mutateAsync(id);
    }
  };

  const handleSendTest = async () => {
    if (!id || !testPhones.trim()) return;

    const recipients = testPhones.split(',').map((p) => p.trim()).filter(Boolean);
    if (recipients.length === 0) return;

    try {
      const result = await sendTestMessage.mutateAsync({
        id,
        data: {
          testRecipients: recipients,
          variableMapping: campaign?.variableMapping
            ? Object.fromEntries(
                Object.entries(campaign.variableMapping).map(([key, val]) => [
                  Number(key),
                  { source: val.source, fieldName: val.fieldName, staticValue: val.staticValue },
                ])
              )
            : {},
        },
      });
      setTestResults(result.results);
    } catch {
      // Error handled by React Query
    }
  };

  const handleMessagesSearchChange = (value: string) => {
    setMessagesSearch(value);
    setMessagesPage(1);
  };

  const handleMessagesStatusChange = (value: string) => {
    setMessagesStatusFilter(value);
    setMessagesPage(1);
  };

  // Formatting
  const formatDate = (dateVal?: string | Date): string => {
    if (!dateVal) return '-';
    const d = typeof dateVal === 'string' ? new Date(dateVal) : dateVal;
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (campaignLoading) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 w-full">
          <div className="flex items-center justify-center py-12" data-testid="campaign-detail-loading">
            <p style={{ color: 'var(--theme-muted-text)' }}>Carregando campanha...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (campaignError || !campaign) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 w-full">
          <div
            className="rounded-xl border p-6 text-center"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
            data-testid="campaign-detail-error"
          >
            <p style={{ color: 'var(--theme-error-color)' }}>
              Erro ao carregar campanha. Tente novamente.
            </p>
            <button
              onClick={() => navigate('/prospecting/campaigns')}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
              data-testid="campaign-detail-error-back"
            >
              Voltar para campanhas
            </button>
          </div>
        </div>
      </div>
    );
  }

  const infoRows: Array<{ k: string; v: string }> = [
    { k: 'Tipo de Audiência', v: AUDIENCE_TYPE_LABELS[campaign.audienceType] || campaign.audienceType },
    { k: 'Tipo de Envio', v: SCHEDULING_TYPE_LABELS[campaign.schedulingType] || campaign.schedulingType },
    { k: 'Criada em', v: formatDate(campaign.createdAt) },
    { k: 'Agendada para', v: campaign.scheduledFor ? formatDate(campaign.scheduledFor) : '-' },
  ];

  const templateBody = getTemplateBodyText(template);

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Toolbar — 3 zonas (DS 3.3): breadcrumb (nunca seta-voltar) + ações condicionais */}
      <header
        className="flex-shrink-0"
        style={{
          backgroundColor: 'var(--theme-header-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
        data-testid="campaign-detail-toolbar"
      >
        <div className="flex flex-col gap-3 px-4 sm:px-6 md:px-8 py-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Zona esquerda — breadcrumb (shared, F25-31: 1º segmento clicável, legenda mobile) */}
          <Breadcrumb
            items={[
              {
                label: 'Prospecção',
                onNavigate: () => navigate('/prospecting/campaigns'),
                testid: 'campaign-detail-breadcrumb-prospecting',
              },
              {
                label: 'Campanhas',
                onNavigate: () => navigate('/prospecting/campaigns'),
                testid: 'campaign-detail-breadcrumb-campaigns',
              },
              { label: campaign.name, testid: 'campaign-detail-breadcrumb-current' },
            ]}
            mobileLegend="Prospecção · Campanha"
            data-testid="campaign-detail-breadcrumb"
          />

          {/* Zona direita — ações */}
          <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
            {/* Teste — sempre visível */}
            <button
              onClick={() => setShowTestSection((prev) => !prev)}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors"
              style={{
                backgroundColor: showTestSection ? 'var(--theme-button-ghost-hover)' : 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
              }}
              data-testid="campaign-detail-test-toggle"
            >
              <Icon name="send" size={16} />
              Teste
            </button>

            {/* Exportar mensagens (CSV) — F08: 38px, borda input-border, ícone 16, hover ghost */}
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 h-[38px] px-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-card-text)',
              }}
              onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
              data-testid="campaign-detail-export"
            >
              <Icon name="download" size={16} />
              {exporting ? 'Exportando…' : 'Exportar'}
            </button>

            {/* Duplicar campanha — sempre visível */}
            <button
              onClick={handleDuplicate}
              disabled={duplicateCampaign.isPending}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
              }}
              data-testid="campaign-detail-duplicate"
            >
              <Icon name="copy" size={16} />
              Duplicar
            </button>

            {/* Ações condicionais por status */}
            {campaign.status === 'draft' && (
              <button
                onClick={handleActivate}
                disabled={activateCampaign.isPending}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--theme-button-primary)' }}
                data-testid="campaign-detail-activate"
              >
                <Icon name="play" size={16} />
                Ativar
              </button>
            )}

            {campaign.status === 'in_progress' && (
              <>
                <button
                  onClick={handlePause}
                  disabled={pauseCampaign.isPending}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
                  data-testid="campaign-detail-pause"
                >
                  <Icon name="pause" size={16} />
                  Pausar
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelCampaign.isPending}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'transparent', color: 'var(--theme-error-color)' }}
                  data-testid="campaign-detail-cancel"
                >
                  <Icon name="stop-circle" size={16} />
                  Cancelar
                </button>
              </>
            )}

            {campaign.status === 'paused' && (
              <>
                <button
                  onClick={handleResume}
                  disabled={resumeCampaign.isPending}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--theme-button-primary)' }}
                  data-testid="campaign-detail-resume"
                >
                  <Icon name="play" size={16} />
                  Retomar
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelCampaign.isPending}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'transparent', color: 'var(--theme-error-color)' }}
                  data-testid="campaign-detail-cancel"
                >
                  <Icon name="stop-circle" size={16} />
                  Cancelar
                </button>
              </>
            )}

            {campaign.status === 'scheduled' && (
              <button
                onClick={handleCancel}
                disabled={cancelCampaign.isPending}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'transparent', color: 'var(--theme-error-color)' }}
                data-testid="campaign-detail-cancel"
              >
                <Icon name="stop-circle" size={16} />
                Cancelar
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Conteúdo rolável */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 w-full">
          {/* Header de identidade */}
          <div className="mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold" style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}>
                {campaign.name}
              </h1>
              <CampaignStatusBadge status={campaign.status} autoPause={campaign.autoPause} data-testid="campaign-status" />
            </div>
            {campaign.description && (
              <p className="text-sm mt-1.5 max-w-[70ch]" style={{ color: 'var(--theme-muted-text)' }}>
                {campaign.description}
              </p>
            )}
          </div>

          {/* Seção de Teste (toggle) */}
          {showTestSection && (
            <div className="mb-6" style={cardStyle} data-testid="campaign-detail-test-section">
              <div className="px-4 py-3 text-sm font-bold" style={cardHeadStyle}>
                Enviar Teste
              </div>
              <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                      Números de telefone (separados por vírgula)
                    </label>
                    <Input
                      placeholder="5511999999999, 5511888888888"
                      value={testPhones}
                      onChange={(e) => setTestPhones(e.target.value)}
                      data-testid="campaign-detail-test-phones"
                    />
                  </div>
                  <button
                    onClick={handleSendTest}
                    disabled={sendTestMessage.isPending || !testPhones.trim()}
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--theme-button-primary)' }}
                    data-testid="campaign-detail-test-send"
                  >
                    <Icon name="send" size={14} />
                    {sendTestMessage.isPending ? 'Enviando...' : 'Enviar Teste'}
                  </button>
                </div>

                {/* Resultados do teste */}
                {testResults && testResults.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="campaign-detail-test-results">
                    {testResults.map((result, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
                        style={{
                          backgroundColor: result.success
                            ? 'var(--theme-success-background)'
                            : 'var(--theme-error-background)',
                        }}
                      >
                        {result.success ? (
                          <Icon name="check-circle" size={14} style={{ color: 'var(--theme-success-color)' }} />
                        ) : (
                          <Icon name="x-circle" size={14} style={{ color: 'var(--theme-error-color)' }} />
                        )}
                        <span
                          style={{
                            color: result.success ? 'var(--theme-success-color)' : 'var(--theme-error-color)',
                          }}
                        >
                          {result.recipientIdentifier}
                          {result.error && ` - ${result.error}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="mb-6">
              <CampaignStatsCards stats={stats} />
            </div>
          )}

          {/* Progress Chart */}
          {stats && stats.totalRecipients > 0 && (
            <div className="mb-6">
              <CampaignProgressChart stats={stats} />
            </div>
          )}

          {/* Previsão de disparo — só depois de iniciada (2026-08-31: campanha "em
              andamento · 0 enviados" sem previsão foi confundida com quebrada e
              recriada 3x; canal lento não é bug, é ritmo) */}
          {campaign.status === 'in_progress' &&
            (campaign.stats.firstDispatchAt || campaign.stats.estimatedCompletionAt) && (
              <p className="text-sm mb-6" style={{ color: 'var(--theme-card-muted)' }} data-testid="campaign-dispatch-forecast">
                {campaign.stats.firstDispatchAt && (
                  <span data-testid="campaign-first-dispatch">
                    Primeira mensagem: {formatDate(campaign.stats.firstDispatchAt)}
                  </span>
                )}
                {campaign.stats.firstDispatchAt && campaign.stats.estimatedCompletionAt && ' · '}
                {campaign.stats.estimatedCompletionAt && (
                  <span data-testid="campaign-estimated-completion">
                    Término previsto: {formatDate(campaign.stats.estimatedCompletionAt)}
                  </span>
                )}
              </p>
            )}

          {/* Informações + Conteúdo — grid 2 colunas (colapsa em 1 abaixo de 900px, mockup .dcols) */}
          <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-4 mb-6">
            {/* Informações da Campanha */}
            <div style={cardStyle} data-testid="campaign-detail-info">
              <div className="px-4 py-3 text-sm font-bold" style={cardHeadStyle}>
                Informações da Campanha
              </div>
              <div>
                {infoRows.map((row, index) => (
                  <div
                    key={row.k}
                    className="flex gap-3 px-4 py-2.5 text-sm"
                    style={index > 0 ? { borderTop: '1px solid var(--theme-card-border)' } : {}}
                  >
                    <span className="flex-shrink-0" style={{ color: 'var(--theme-muted-text)', width: 140 }}>
                      {row.k}
                    </span>
                    <span className="font-medium min-w-0 break-words" style={{ color: 'var(--theme-card-text)' }}>
                      {row.v}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Conteúdo — preview do corpo do template (mockup .tprev) */}
            <div style={cardStyle} data-testid="campaign-detail-content">
              <div className="px-4 py-3 text-sm font-bold" style={cardHeadStyle}>
                Conteúdo
              </div>
              <div className="p-4">
                <div
                  className="whitespace-pre-wrap break-words"
                  style={{
                    backgroundColor: 'var(--theme-page-background)',
                    border: '1px solid var(--theme-card-border)',
                    borderRadius: 10,
                    padding: 14,
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: templateBody ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                  }}
                  data-testid="campaign-detail-content-preview"
                >
                  {templateBody ?? 'Conteúdo do template não disponível'}
                </div>
              </div>
            </div>
          </div>

          {/* Messages Table */}
          <CampaignMessagesTable
            data={messagesData}
            isLoading={messagesLoading}
            search={messagesSearch}
            onSearchChange={handleMessagesSearchChange}
            statusFilter={messagesStatusFilter}
            onStatusFilterChange={handleMessagesStatusChange}
            onPageChange={setMessagesPage}
          />
        </div>
      </div>
    </div>
  );
};
